/**
 * Making the shared film on the visitor's device, for the phone's share view and the
 * computer's download: one film per shape and visit, progress, cancel, and carrying on
 * once after the page was hidden mid-film.
 */
import { captureException, withScope } from "@sentry/browser";
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import {
  abortWhenHidden,
  canEncodeVideo,
  EncodeError,
  encodeVideo,
} from "../slideshow/encode";
import { LIFE_SELECTION, makeFilm } from "../slideshow/film";
import type { Format, PageContent } from "../slideshow/playlist";
import { fileBaseName, videoFile } from "../slideshow/share";
import { track } from "../slideshow/track";
import type { FilmKind } from "../slideshow/types";
import {
  assets,
  loadPageContent,
  musicFor,
  slideshowTexts,
  usePrefersReducedMotion,
  viewer,
} from "./useSlideshow";
import { useWakeLock } from "./useWakeLock";

/**
 * The film's size in each shape: stories in 9:16, posts, YouTube and TV in 16:9. Full HD,
 * since phones and laptops show it enlarged.
 */
export const FILM_SIZE: Record<Format, [number, number]> = {
  landscape: [1920, 1080],
  portrait: [1080, 1920],
};
/** Past this, a film still under half done gets the "this takes a while" note. */
const SLOW_AFTER_MS = 20_000;
const REASON_MAX_LENGTH = 120;

export type FilmExport = ReturnType<typeof useFilmExport>;

export function useFilmExport(
  content: () => PageContent,
  name: () => string,
  isPhone: boolean
) {
  const texts = slideshowTexts();
  const reducedMotion = usePrefersReducedMotion();
  const wakeLock = useWakeLock();

  /** No WebCodecs, or making the film failed: share the link instead, without an error. */
  const videoMode = ref(canEncodeVideo());
  const making = ref(false);
  const progress = ref(0);
  const slow = ref(false);
  /** "resuming" after the page was hidden once; "failed" after twice. */
  const interrupted = ref<"no" | "resuming" | "failed">("no");

  // The panel can open before faces and photo sizes are known; the film is always made with
  // them, so portrait photos and faces are framed as on the page.
  const fullContent = shallowRef<PageContent | null>(null);
  const contentReady = loadPageContent().then((full) => {
    fullContent.value = full;
  });

  // One file per film and shape: the slideshow's is never handed out as the life story.
  const files = new Map<string, File>();
  const pending = new Map<string, Promise<File | null>>();
  const key = (format: Format, kind: FilmKind = viewer.kind) =>
    `${kind}:${format}`;
  let controller: AbortController | null = null;
  let slowTimer = 0;
  let interruptions = 0;
  let lastFormat: Format = isPhone ? "portrait" : "landscape";
  let trigger: "open" | "share" = "share";

  /** "…-minnesfilm" or "…-tidslinje". */
  const fileSuffix = (kind: FilmKind) =>
    kind === "life" ? texts.lifeFileSuffix : texts.fileSuffix;

  const percent = computed(() => Math.round(progress.value * 100));
  const notice = computed(() => {
    if (interrupted.value === "resuming") {
      return texts.resumed;
    }
    if (interrupted.value === "failed") {
      return texts.interruptedTwice;
    }
    if (slow.value) {
      return isPhone ? texts.slowPhone : texts.slowComputer;
    }
    return "";
  });

  /**
   * The film's timeline in a shape: the page's own selection (the preview plays exactly
   * this). The life story always shares the whole life as its selection within 2:45.
   */
  function film(format: Format, kind: FilmKind = viewer.kind) {
    return makeFilm(
      fullContent.value ?? content(),
      format,
      reducedMotion.value,
      kind === "life"
        ? { ...LIFE_SELECTION, eyebrow: texts.lifeTitle, kind }
        : {}
    ).timeline;
  }

  function failureReason(e: unknown, reason: string) {
    const detail =
      e instanceof Error && e.message !== reason ? `: ${e.message}` : "";
    return `${reason}${detail}`.slice(0, REASON_MAX_LENGTH);
  }

  // The film's kind is read once, by the caller: closing the viewer sets it back to the
  // slideshow while a life story may still be finishing.
  async function make(format: Format, kind: FilmKind): Promise<File | null> {
    const [width, height] = FILM_SIZE[format];
    // One film at a time: a new one stops the one in progress, and each run cleans up only
    // what is still its own.
    controller?.abort("cancel");
    const own = new AbortController();
    controller = own;
    const stopWatching = abortWhenHidden(own);
    making.value = true;
    progress.value = 0;
    slow.value = false;
    slowTimer = window.setTimeout(() => {
      slow.value = progress.value < 0.5;
    }, SLOW_AFTER_MS);
    wakeLock.hold();
    try {
      await contentReady;
      const result = await encodeVideo({
        assets,
        height,
        musicUrl: musicFor(content(), kind),
        onProgress: (p) => {
          progress.value = p;
        },
        reducedMotion: reducedMotion.value,
        signal: own.signal,
        timeline: film(format, kind),
        width,
      });
      if (own.signal.aborted) {
        // Finished just as it was cancelled or the motion setting changed: not this film.
        return null;
      }
      const file = videoFile(
        result.blob,
        fileBaseName(name(), fileSuffix(kind)),
        result.extension
      );
      files.set(key(format, kind), file);
      interrupted.value = "no";
      track({
        action: "video_ready",
        audio: !!result.audioCodec,
        duration: result.duration,
        format,
        seconds: result.seconds,
        trigger,
      });
      return file;
    } catch (e) {
      const reason = e instanceof EncodeError ? e.reason : "failed";
      if (reason === "aborted") {
        track({ action: "video_cancelled", format, trigger });
      } else if (reason === "hidden") {
        // The visitor left the page mid-film (on phones often before asking for it): not a
        // failure, so it is counted apart and not reported.
        interruptions += 1;
        interrupted.value = interruptions > 1 ? "failed" : "resuming";
        track({ action: "video_interrupted", format, trigger });
      } else {
        // Can't make the film here: share the link instead, quietly.
        videoMode.value = false;
        track({
          action: "video_failed",
          format,
          reason: failureReason(e, reason),
          trigger,
        });
        withScope((scope) => {
          scope.setTag("slideshow.reason", reason);
          scope.setTag("slideshow.format", format);
          scope.setTag("slideshow.trigger", trigger);
          captureException(e);
        });
      }
      return null;
    } finally {
      stopWatching();
      if (controller === own) {
        controller = null;
        making.value = false;
        clearTimeout(slowTimer);
        wakeLock.release();
      }
    }
  }

  /**
   * The film in a shape, made once per visit; a second ask waits for the one in progress.
   * `by` says what asked first: the slideshow opening ahead of time, or a tap on share.
   */
  function ensureFile(
    format: Format,
    by: "open" | "share" = "share"
  ): Promise<File | null> {
    lastFormat = format;
    const kind = viewer.kind;
    const done = files.get(key(format, kind));
    if (done) {
      return Promise.resolve(done);
    }
    let run = pending.get(key(format, kind));
    if (!run) {
      trigger = by;
      const started: Promise<File | null> = make(format, kind).finally(() => {
        if (pending.get(key(format, kind)) === started) {
          pending.delete(key(format, kind));
        }
      });
      run = started;
      pending.set(key(format, kind), run);
    }
    return run;
  }

  const readyFile = (format: Format) => files.get(key(format)) ?? null;

  function cancel() {
    controller?.abort("cancel");
  }

  /** Forget made films and stop the one in progress (the motion setting changed). */
  function reset() {
    controller?.abort("cancel");
    pending.clear();
    files.clear();
  }

  function retry() {
    interruptions = 0;
    interrupted.value = "no";
    ensureFile(lastFormat);
  }

  /** Back on the page after it was hidden mid-film: carry on by ourselves, once. */
  function onVisible() {
    if (
      !document.hidden &&
      interrupted.value === "resuming" &&
      videoMode.value
    ) {
      ensureFile(lastFormat);
    }
  }

  onMounted(() => document.addEventListener("visibilitychange", onVisible));
  onBeforeUnmount(() => {
    controller?.abort("cancel");
    document.removeEventListener("visibilitychange", onVisible);
  });

  return {
    cancel,
    ensureFile,
    film,
    fullContent,
    interrupted,
    making,
    notice,
    percent,
    progress,
    readyFile,
    reducedMotion,
    reset,
    retry,
    videoMode,
  };
}
